<template>
  <div
    :data-auto-testid="ELEMENT_PROGRESS_HISTORY_TEST_ID"
    :data-testid="ELEMENT_PROGRESS_HISTORY_TEST_ID"
    class="ph-Wrapper"
  >
    <ActivityListBase ref="activity-list" :activity-type="PROGRESS_HISTORY" :objective="objective">
      <template #activity="{ activity }">
        <div
          :class="{
            'ph-Item-with-action': hasAction
          }"
          :data-auto-testid="ELEMENT_PROGRESS_HISTORY_ITEM_TEST_ID"
          :data-testid="ELEMENT_PROGRESS_HISTORY_ITEM_TEST_ID"
          class="ph-Item"
        >
          <AppAvatar :avatar-url="activity.authorAvatarUrl" no-margins />
          <div class="ph-Content">
            <ActivityHeader
              :action="$t('action.changed_the')"
              :name="activity.authorName"
              :time="getActivityTime(activity.date)"
            >
              <template #object>
                <strong>{{ $t('action.current_value') }}</strong>
              </template>
            </ActivityHeader>
            <div class="ph-Description">
              <ContributeLine class="ph-ContributeLine" />
              <span
                :data-auto-testid="ELEMENT_PROGRESS_HISTORY_CONTENT_TEST_ID"
                :data-testid="ELEMENT_PROGRESS_HISTORY_CONTENT_TEST_ID"
                class="ph-DescriptionText"
              >
                {{ getValue(activity.fromValue) }}
                <AppIcon height="24" icon-name="arrow-right-next" width="24" />
                {{ getValue(activity.toValue) }}
              </span>
            </div>
          </div>
          <AppButton
            v-if="hasAction"
            v-tippy="{
              placement: 'top',
              content: !currentUserCanDeleteProgressHistory(activity)
                ? $t('checkin.permissionless_delete_progress_item')
                : null
            }"
            :data-auto-testid="REMOVE_PROGRESS_HISTORY_ITEM_BUTTON_TEST_ID"
            :data-testid="REMOVE_PROGRESS_HISTORY_ITEM_BUTTON_TEST_ID"
            :disable="!currentUserCanDeleteProgressHistory(activity)"
            class="ph-DeleteBtn"
            height="24"
            icon="delete-next"
            remove-padding
            size="sm"
            type="ghost-next"
            width="24"
            @click="showDeleteDialog(activity.id)"
          />
        </div>
      </template>
      <template #no-results>
        <div class="ph-NoItems">
          {{ $t('objective.progress_history_empty') }}
        </div>
      </template>
      <template #loader>
        <ActivityLogLoader />
      </template>
    </ActivityListBase>

    <AppDeleteModal
      v-if="!isOkrElementClosed(objective)"
      :loading="isLoading"
      :show="showConfirmDeleteDialog"
      :title="$t('objective.delete_progress_history.title')"
      keypath="objective.delete_progress_history.message"
      @on-confirm="onConfirmDelete"
      @on-close="onCloseDeleteDialog"
    >
      <template #i18n-t:break><br /> </template>
      <template #i18n-t:entity>
        {{ $t('common.progress_history_record').toLowerCase() }}
      </template>
    </AppDeleteModal>
  </div>
</template>

<script setup>
import dayjs from 'dayjs'
import { computed, ref, useTemplateRef } from 'vue'
import { useI18n } from 'vue-i18n'

import ObjectivesInfoApiHandler from '@/api/okr-elements'
import { PROGRESS_HISTORY } from '@/utils/activities'
import { MILESTONE_DAY_FORMAT } from '@/utils/date'
import { handleError } from '@/utils/error-handling'
import { getResolvedUnitForChart } from '@/utils/objective-chart'
import { isOkrElementClosed } from '@/utils/objectives'
import { currentUserCanDeleteProgressHistory } from '@/utils/progress-history'

import {
  ELEMENT_PROGRESS_HISTORY_CONTENT_TEST_ID,
  ELEMENT_PROGRESS_HISTORY_ITEM_TEST_ID,
  ELEMENT_PROGRESS_HISTORY_TEST_ID,
  REMOVE_PROGRESS_HISTORY_ITEM_BUTTON_TEST_ID
} from '@/components/form/jest-helpers'
import ContributeLine from '@/components/objectives/forms/ContributeLine'
import ActivityHeader from '@/components/objectives/forms/objective-form/ActivityHeader'
import ActivityListBase from '@/components/objectives/forms/objective-form/ActivityListBase'
import AppAvatar from '@/components/ui/AppAvatar/AppAvatar'
import AppButton from '@/components/ui/AppButton/AppButton'
import AppDeleteModal from '@/components/ui/AppDeleteModal/AppDeleteModal'
import AppIcon from '@/components/ui/AppIcon/AppIcon'
import ActivityLogLoader from '@/components/ui/SkeletonLoaders/ActivityLogLoader'

defineOptions({
  name: 'ProgressHistory'
})

const props = defineProps({
  objective: { type: Object, required: true },

  unit: {
    type: String,
    default: ''
  }
})

const emit = defineEmits({
  'updated-progress-history': null
})

const isLoading = ref(false)
const showConfirmDeleteDialog = ref(false)
const deleteItemId = ref(null)
const activityList = useTemplateRef('activity-list')

const hasAction = computed(() => !isOkrElementClosed(props.objective))

const getValue = value => {
  return `${value || 0}${getResolvedUnitForChart(props.unit)}`
}

const showDeleteDialog = id => {
  showConfirmDeleteDialog.value = true
  deleteItemId.value = id
}

const refreshData = () => {
  if (activityList.value && activityList.value.refreshData) {
    activityList.value.refreshData()
  }
}

const onConfirmDelete = async () => {
  try {
    isLoading.value = true
    const api = new ObjectivesInfoApiHandler()
    const payload = await api.deleteProgressHistoryElement({
      id: deleteItemId.value,
      elementId: props.objective.id
    })
    emit('updated-progress-history', payload)
    onCloseDeleteDialog()
    refreshData()
  } catch (error) {
    handleError({ error })
  } finally {
    isLoading.value = false
  }
}

const onCloseDeleteDialog = () => {
  showConfirmDeleteDialog.value = false
  deleteItemId.value = null
}

const { t } = useI18n()

const getActivityTime = date => {
  const dateObject = dayjs(date)
  return dateObject.isToday()
    ? t('filter.today').toLowerCase()
    : dateObject.format(MILESTONE_DAY_FORMAT)
}

defineExpose({
  refreshData
})
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/activities';

.ph-Wrapper {
  @extend %activities-Wrapper;
}

.ph-Item {
  @extend %activities-Item;

  .ph-DeleteBtn {
    display: none;
    color: $dark-2;
  }

  &:hover {
    .ph-DeleteBtn {
      display: block;
    }
  }
}

.ph-Content {
  @extend %activities-Content;

  .ph-Item-with-action:hover & {
    width: calc(100% - 64px);
  }
}

.ph-Description {
  @extend %activities-Description;
}

.ph-DescriptionText {
  @extend %activities-DescriptionText;
  display: flex;
  align-items: center;
}

.ph-NoItems {
  @extend %activities-NoItems;
}

.ph-ContributeLine {
  @extend %activities-ContributeLine;
}
</style>
